import { useState } from 'react';
import { cn } from '@klaro7/ui';

/* -------------------------------------------------------------------------- */
/*  Types                                                                      */
/* -------------------------------------------------------------------------- */

export interface SerpPreviewData {
  title: string;
  description: string;
  path: string;
}

export interface SocialPreviewData {
  title: string;
  description: string;
  imageUrl?: string;
  siteName: string;
}

export interface JsonLdPreviewBlock {
  label: string;
  data: Record<string, unknown> | null;
}

type PreviewTab = 'google' | 'social' | 'jsonld';
type DeviceMode = 'desktop' | 'mobile';

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function truncate(value: string, max: number): string {
  return value.length > max ? `${value.slice(0, max)}…` : value;
}

function buildBreadcrumb(path: string): string {
  const clean = path && path !== '/' ? path.replace(/^\//, '').replace(/\//g, ' › ') : '';
  return clean ? `app.klaro7.com › ${clean}` : 'app.klaro7.com';
}

/* -------------------------------------------------------------------------- */
/*  Root panel                                                                 */
/* -------------------------------------------------------------------------- */

export function SeoPreviewPanel({
  serp,
  social,
  jsonLdBlocks,
}: {
  serp: SerpPreviewData;
  social: SocialPreviewData;
  jsonLdBlocks: JsonLdPreviewBlock[];
}) {
  const [tab, setTab] = useState<PreviewTab>('google');

  return (
    <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-soft">
      <div className="flex items-center justify-between border-b border-neutral-200 px-4 py-3">
        <h3 className="text-sm font-semibold text-neutral-900">Anteprima live</h3>
        <div className="flex items-center gap-1.5">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
          </span>
          <span className="text-[11px] font-medium uppercase tracking-wide text-neutral-400">
            Live
          </span>
        </div>
      </div>

      <div className="px-4 pt-3">
        <SegmentedControl
          value={tab}
          onChange={setTab}
          options={[
            { value: 'google', label: 'Google' },
            { value: 'social', label: 'Social' },
            { value: 'jsonld', label: 'JSON-LD' },
          ]}
        />
      </div>

      <div className="p-4">
        {tab === 'google' && <GoogleTab data={serp} />}
        {tab === 'social' && <SocialTab data={social} />}
        {tab === 'jsonld' && <JsonLdTab blocks={jsonLdBlocks} />}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Segmented control (generic, private)                                      */
/* -------------------------------------------------------------------------- */

function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  size = 'md',
}: {
  value: T;
  onChange: (value: T) => void;
  options: { value: T; label: string }[];
  size?: 'md' | 'sm';
}) {
  const activeIndex = options.findIndex((o) => o.value === value);
  return (
    <div
      className={cn(
        'relative grid rounded-xl bg-neutral-100 p-1',
        size === 'sm' ? 'text-xs' : 'text-sm'
      )}
      style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}
    >
      <span
        className="absolute inset-y-1 rounded-lg bg-white shadow-sm transition-all duration-200 ease-out"
        style={{
          width: `calc(${100 / options.length}% - 0.25rem)`,
          transform: `translateX(calc(${activeIndex * 100}% + ${activeIndex * 0.25}rem))`,
        }}
      />
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          className={cn(
            'relative z-10 rounded-lg px-2 font-medium transition-colors duration-200',
            size === 'sm' ? 'py-1' : 'py-1.5',
            value === option.value ? 'text-brand-navy' : 'text-neutral-500 hover:text-neutral-700'
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Google tab                                                                 */
/* -------------------------------------------------------------------------- */

function GoogleTab({ data }: { data: SerpPreviewData }) {
  const [device, setDevice] = useState<DeviceMode>('desktop');
  const titleMax = 60;
  const descMax = device === 'desktop' ? 160 : 120;
  const breadcrumb = buildBreadcrumb(data.path);

  return (
    <div className="space-y-3">
      <SegmentedControl
        value={device}
        onChange={setDevice}
        size="sm"
        options={[
          { value: 'desktop', label: 'Desktop' },
          { value: 'mobile', label: 'Mobile' },
        ]}
      />

      <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-4">
        <div
          className={cn(
            'mx-auto bg-white font-[arial]',
            device === 'mobile'
              ? 'max-w-[280px] rounded-2xl border border-neutral-200 p-3'
              : 'max-w-md'
          )}
        >
          <div className="flex items-center gap-2">
            <div
              className={cn(
                'flex items-center justify-center rounded-full border border-neutral-200 bg-white',
                device === 'mobile' ? 'h-5 w-5' : 'h-7 w-7'
              )}
            >
              <img src="/images/logo-navy.png" alt="" className={device === 'mobile' ? 'h-3 w-3 object-contain' : 'h-4 w-4 object-contain'} />
            </div>
            <div className="min-w-0 leading-tight">
              <p className={cn('text-neutral-800', device === 'mobile' ? 'text-xs' : 'text-sm')}>
                Klaro7
              </p>
              <p className={cn('truncate text-neutral-500', device === 'mobile' ? 'text-[10px]' : 'text-xs')}>
                {breadcrumb}
              </p>
            </div>
          </div>
          <p
            className={cn(
              'mt-1.5 cursor-pointer leading-snug text-[#1a0dab] hover:underline',
              device === 'mobile' ? 'text-base' : 'text-xl'
            )}
          >
            {truncate(data.title, titleMax) || 'Titolo della pagina'}
          </p>
          <p
            className={cn(
              'mt-1 leading-snug text-neutral-600',
              device === 'mobile' ? 'text-xs' : 'text-sm'
            )}
          >
            {truncate(data.description, descMax) || 'La meta description apparirà qui.'}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-1">
        <CharCounter label="title" length={data.title.length} ideal={titleMax} />
        <CharCounter label="description" length={data.description.length} ideal={descMax} />
      </div>
    </div>
  );
}

function CharCounter({ label, length, ideal }: { label: string; length: number; ideal: number }) {
  const over = length > ideal;
  return (
    <span className={cn('text-xs', over ? 'text-amber-600' : 'text-neutral-400')}>
      {label} {length}/{ideal}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/*  Social tab                                                                 */
/* -------------------------------------------------------------------------- */

function SocialTab({ data }: { data: SocialPreviewData }) {
  return (
    <div className="overflow-hidden rounded-xl border border-neutral-200 shadow-sm">
      <div className="relative aspect-[1.91/1] w-full overflow-hidden bg-neutral-100">
        {data.imageUrl ? (
          <img src={data.imageUrl} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-brand-navy to-brand-teal">
            <img src="/images/logo-navy.png" alt="" className="h-10 w-10 object-contain opacity-90" />
          </div>
        )}
      </div>
      <div className="space-y-1 bg-neutral-50 p-3">
        <p className="text-[11px] uppercase tracking-wide text-neutral-400">{data.siteName}</p>
        <p className="line-clamp-2 text-sm font-bold leading-snug text-neutral-900">
          {data.title || 'Titolo della pagina'}
        </p>
        <p className="line-clamp-2 text-xs leading-snug text-neutral-500">
          {data.description || 'La description apparirà qui.'}
        </p>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  JSON-LD tab                                                                */
/* -------------------------------------------------------------------------- */

function JsonLdTab({ blocks }: { blocks: JsonLdPreviewBlock[] }) {
  if (blocks.length === 0) {
    return <p className="py-6 text-center text-sm text-neutral-400">Nessun blocco JSON-LD configurato.</p>;
  }

  return (
    <div className="space-y-3">
      {blocks.map((block) => (
        <JsonLdBlockCard key={block.label} block={block} />
      ))}
    </div>
  );
}

function JsonLdBlockCard({ block }: { block: JsonLdPreviewBlock }) {
  const type = typeof block.data?.['@type'] === 'string' ? (block.data['@type'] as string) : null;

  return (
    <div className="rounded-xl border border-neutral-200 p-3">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-sm font-medium text-neutral-800">{block.label}</p>
        {block.data ? (
          type && (
            <span className="rounded-full bg-brand-teal-50 px-2 py-0.5 text-[11px] font-medium text-brand-teal-600">
              {type}
            </span>
          )
        ) : (
          <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[11px] font-medium text-neutral-400">
            Disattivato
          </span>
        )}
      </div>
      {block.data ? (
        <pre className="max-h-64 overflow-auto rounded-lg bg-[#0f172a] p-3 font-mono text-[11px] leading-relaxed text-emerald-400">
          {JSON.stringify(block.data, null, 2)}
        </pre>
      ) : (
        <p className="text-xs text-neutral-400">Blocco non attivo per questa pagina.</p>
      )}
    </div>
  );
}
